import React, { useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View, Image, TouchableOpacity, Modal, Platform } from 'react-native';
import { Chip, Text, Button } from 'react-native-paper';
import FaviconStack from '../../../components/FaviconStack';
import { useThemeSettings } from '../../../ThemeProvider';
import { renderContent } from '../helpers/renderContent';
import getGlobalStyles from '../../../globalStyles';
import { formatArticleDate } from '../../../shared/helpers/formatDate';
import { usePlatform } from '../../../providers/PlatformProvider';
import { FULL_HEIGHT, FULL_WIDTH } from '../../../shared/constants/dimension';
import { CustomIcon } from '../../../components/CustomIcon';
import { getImageUri } from '../../../shared/helpers/utils';
import WebView from 'react-native-webview';
import { useActiveArticleStore } from "../../../store/activeArticleStore";
import { ImpactTag } from '../../../components/ImpactTag';

interface NewsDetailsCardProps {
  id: string;
  description?: string;
  impactTag?: string;
  contentType: string;
  content: string[] | string;
  sourceCount: number;
  favicons: string[];
  date: string;
  image: string | null;
  imageSourceUrl: string | null;
  title: string;
  onShare?: () => void;
} 

export const NewsDetailsCard: React.FC<NewsDetailsCardProps> = ({
  id,
  description,
  impactTag,
  contentType,
  content,
  sourceCount,
  favicons,
  date,
  image,
  imageSourceUrl,
  title,
  onShare,
}) => {
  const { theme } = useThemeSettings();
  const { showWebVersion } = usePlatform();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const scrollRef = useRef<ScrollView | null>(null);
  const isNative = Platform.OS !== 'web';
  const [url, setUrl] = useState<string | null>(null);
  return (
    <View style={[gStyles.centerColumn, { height: FULL_HEIGHT }]}>
      <View style={{ height: '25%' }}>
        <Image source={getImageUri(image!)} style={styles.image} />

        <View style={styles.shadowOverlay} />
        {Platform.OS === "web" ? null : (
          <View style={styles.titleOverlay}>
            <Text style={{ color: 'white', fontSize: 18, fontWeight: '700', textTransform: "capitalize" }} >
              {title}
            </Text>
            {imageSourceUrl && <TouchableOpacity style={gStyles.categoryChip} onPress={() =>
                  imageSourceUrl
                    ? Platform.OS === "web"
                      ? window.open(imageSourceUrl, "_blank").focus()
                      : setUrl(imageSourceUrl)
                    : null
                }
              >
                <CustomIcon
                  icon="information-outline"
                  size={12}
                  color={theme?.colors.white}
                />
              </TouchableOpacity>}
          </View>
        )}

        {Platform.OS === "web" ? null : onShare && (
          <TouchableOpacity
            style={[gStyles.buttonStyle, gStyles.buttonCircle,
                  {
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: theme?.colors?.btnBgFill,
                  },
                ]}
            onPress={onShare}
          >
            <CustomIcon icon="share" size={16} color={theme?.colors?.primary} />
          </TouchableOpacity>
        )}
        <ImpactTag tag={impactTag} side="left" top={8.5} offset={8} />

        {Platform.OS === "web" ? (
          <View>
            <Chip
              mode="flat"
              style={[gStyles.categoryChip,
                {
                  alignSelf: "flex-start",
                  position: "absolute",
                  left: 12,
                  bottom: 12,
                },
              ]}
              textStyle={{
                ...theme.fonts.titleSmall,
                marginHorizontal: 8,
                color: theme?.colors?.white,
              }}
            >
              {formatArticleDate(date)}
            </Chip>
            <View style={styles.sourcesAbsoluteContainer}>
              <FaviconStack
                favicons={favicons}
                count={0}
                articleId={id}
              />
              <TouchableOpacity style={gStyles.categoryChip} onPress={() =>
                  imageSourceUrl
                    ? Platform.OS === "web"
                      ? window.open(imageSourceUrl, "_blank").focus()
                      : setUrl(imageSourceUrl)
                    : null
                }
              >
                <CustomIcon
                  icon="information-outline"
                  size={12}
                  color={theme?.colors.white}
                />
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
        
      </View>
      <View style={[styles.contentContainer]}>
        
        {!showWebVersion ? null : <View style={styles.bookmark}></View>}
        <ScrollView
          ref={scrollRef}
          showsVerticalScrollIndicator={false}
          scrollEnabled={ true}
        >
          {/* <Text variant='bodyMedium'>{description}</Text> */}
          {content && (<>{renderContent(contentType, content, theme, { marginTop: 16 })}</>)}
        </ScrollView>

        {!showWebVersion && (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 }}>
            <FaviconStack favicons={favicons} count={0} articleId={id} />
            <Text style={{ color: theme?.colors?.textPrimary, fontSize: 12 }}>
              {formatArticleDate(date)}
            </Text>
          </View>
        )}
      </View>
      <Modal visible={url !== null} animationType="slide">
        <View style={{ flex: 1, marginTop : 15 }}>
          {/* <Button onPress={() => setUrl(null)}>Close</Button> */}
          <TouchableOpacity onPress={() => setUrl(null)} style={{alignSelf : "flex-end", paddingRight : 20, paddingBottom : 10}}>
          <View style={[ styles.closeCircle, { borderColor: theme?.colors?.black }]}>
            <Text style={[styles.closeText, { color: theme?.colors?.black }]}>✕</Text>
          </View>
          </TouchableOpacity>
          <WebView source={{ uri: url || '' }} style={{ flex: 1 }} />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  image: {
    width: FULL_WIDTH,
    height: FULL_WIDTH,
  },
  shadowOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: FULL_WIDTH,
    justifyContent: 'flex-end',
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 15,
    paddingBottom: 12,
    width: FULL_WIDTH
  },
  bookmark: {
    position: 'absolute', right: 12, top: 5
  },
  sourcesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sourcesAbsoluteContainer: {
    position: 'absolute',
    bottom: 0,
    right: 2,
    gap:0,
    flexDirection: 'row',
    alignItems:'flex-end'
  },
  sourceLogos: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleOverlay: {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 10,
  padding: 12,
  flexDirection : "row",
  justifyContent: "space-between",
  alignItems : "flex-end"
},
closeText: {
    fontSize: 18,
    fontWeight: "900",
  },
  closeCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 2,
    justifyContent: "center",
    alignItems: "center",
  },
});